// ✅ CLEANED & UPDATED AddEditProject

import React, { useEffect, useMemo, useState } from "react";
import Button from "@/components/Base/Button";
import FormInput from "@/components/Base/Form/FormInput";
import FormSelect from "@/components/Base/Form/FormSelect";
import Lucide from "@/components/Base/Lucide";
import clsx from "clsx";
import axios from "axios";
import { getToken, getRole } from "@/utils/auth";
import { useNavigate, useParams } from "react-router-dom";
import Litepicker from "@/components/Base/Litepicker";

// Fix date parsing to avoid timezone shift
function parseLocalDateOnly(input: string): string {
    if (!input || typeof input !== "string") {
        throw new Error("Invalid date input: " + input);
    }

    const date = new Date(input);
    if (isNaN(date.getTime())) {
        throw new Error("Parsed invalid date from: " + input);
    }

    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0"); // Month is 0-based
    const day = String(date.getDate()).padStart(2, "0");

    return `${year}-${month}-${day}`; // Returns YYYY-MM-DD
}

type Mode = "add" | "edit";

interface Props {
    mode?: Mode;
}

interface CompanyOption {
    id: string;
    businessName: string;
}

interface FormData {
    name: string;
    startDate: string;
    endDate: string;
    companyId?: string;
}

const EMPTY_FORM: FormData = {
    name: "",
    startDate: "",
    endDate: "",
    companyId: "",
};

export default function AddEditProject({ mode: modeProp }: Props) {
    const { id } = useParams();
    const mode: Mode = useMemo(() => (modeProp ? modeProp : id ? "edit" : "add"), [modeProp, id]);
    const [formData, setFormData] = useState<FormData>(EMPTY_FORM);
    const [companies, setCompanies] = useState<CompanyOption[]>([]);
    const [loading, setLoading] = useState(mode === "edit");
    const [error, setError] = useState("");
    const navigate = useNavigate();
    const role = getRole();

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
    };

    // Fetch companies (for SUPERADMIN dropdown)
    useEffect(() => {
        if (role !== "SUPERADMIN") return;
        (async () => {
            try {
                const res = await axios.get(`${import.meta.env.VITE_API_URL}/companies`, {
                    headers: { Authorization: `Bearer ${getToken()}` },
                });
                setCompanies(res.data?.items || []);
            } catch (err) {
                console.error("Error fetching companies:", err);
            }
        })();
    }, [role]);

    // Fetch project if editing
    useEffect(() => {
        if (mode !== "edit" || !id) return;
        (async () => {
            try {
                setLoading(true);
                const res = await axios.get(`${import.meta.env.VITE_API_URL}/projects/${id}`, {
                    headers: { Authorization: `Bearer ${getToken()}` },
                });
                console.log('Project data response:', res.data);
                const p = res.data.data; // Access the data property from the response
                if (!p) {
                    throw new Error('Project data not found in response');
                }
                setFormData({
                    name: p.name || "",
                    startDate: p.startDate?.slice(0, 10) || "",
                    endDate: p.endDate?.slice(0, 10) || "",
                    companyId: p.companyId || "",
                });
            } catch (err: any) {
                // Handle authentication errors
                if (err?.response?.status === 401) {
                    // Clear invalid token
                    localStorage.removeItem("token");
                    localStorage.removeItem("role");
                    navigate("/login");
                    return;
                }
                setError(err?.response?.data?.error || "Failed to load project");
            } finally {
                setLoading(false);
            }
        })();
    }, [mode, id]);

    const handleSubmit = async () => {
        try {
            setError("");
            const token = getToken();
            const payload = { ...formData };
            // For ADMIN users, we don't need to set companyId as the backend will handle it based on the JWT token
            if (!payload.name) throw new Error("Project name is required");

            if (mode === "edit" && id) {
                await axios.put(`${import.meta.env.VITE_API_URL}/projects/${id}`, payload, {
                    headers: { Authorization: `Bearer ${token}` },
                });
            } else {
                await axios.post(`${import.meta.env.VITE_API_URL}/projects`, payload, {
                    headers: { Authorization: `Bearer ${token}` },
                });
            }

            // If ADMIN, we don't need to pass companyId as backend will filter
            // If SUPERADMIN and we have companyId in form, maintain the filter
            if (role === "SUPERADMIN" && formData.companyId) {
                navigate(`/projects?companyId=${formData.companyId}`);
            } else {
                navigate("/projects");
            }
        } catch (err: any) {
            const msg = err?.response?.data?.error;
            if (err?.response?.status === 401) {
                // Handle authentication errors
                localStorage.removeItem("token");
                localStorage.removeItem("role");
                navigate("/login");
                return;
            } else if (err?.response?.status === 409 && msg?.includes("exists")) {
                setError("❌ Project name already exists. Please use another name.");
            } else {
                setError(msg || "Submission failed");
            }
        }
    };

    return (
        <div className="grid grid-cols-12 gap-y-10 gap-x-6">
            <div className="col-span-12 sm:col-span-10 sm:col-start-2">
                <div className="flex flex-col lg:items-center lg:flex-row gap-y-2">
                    <div
                        className={clsx([
                            "flex items-center lg:justify-center flex-1 lg:first:justify-start lg:last:justify-end group active",
                            "after:hidden before:hidden after:lg:block before:lg:block",
                            "first:after:content-[''] first:after:w-full first:after:bg-slate-300/60 first:after:h-[2px] first:after:ml-5 group-[.mode--light]:first:after:bg-slate-300/20",
                            "last:before:content-[''] last:before:w-full last:before:bg-slate-300/60 last:before:h-[2px] last:before:mr-5 group-[.mode--light]:last:before:bg-slate-300/20",
                            "last:after:hidden after:content-[''] after:w-full after:bg-slate-300/60 after:h-[2px] after:ml-5 group-[.mode--light]:after:bg-slate-300/20",
                            "first:before:hidden before:content-[''] before:w-full before:bg-slate-300/60 before:h-[2px] before:mr-5 group-[.mode--light]:before:bg-slate-300/20",
                        ])}
                    >
                        <div className="flex items-center">
                            <div className="bg-white border rounded-full group-[.mode--light]:!bg-transparent group-[.active]:bg-primary group-[.active]:text-white group-[.mode--light]:!text-slate-200 group-[.mode--light]:!border-white/[0.25] [.group.mode--light_.group.active_&]:!bg-white/[0.12] [.group.mode--light_.group.active_&]:!border-white/[0.15] dark:[.group.mode--light_.group.active_&]:!bg-darkmode-800/30 dark:[.group.mode--light_.group.active_&]:!border-white/10 dark:group-[.mode--light]:!border-white/10 dark:bg-transparent">
                                <div className="flex items-center justify-center w-10 h-10">
                                    1
                                </div>
                            </div>
                            <div className="ml-3.5 group-[.mode--light]:!text-slate-300 font-medium whitespace-nowrap text-slate-500 group-[.active]:text-current [.group.mode--light_.group.active_&]:!text-slate-100">
                                {mode === "edit" ? " Edit " : ""}
                                Project Information
                            </div>
                        </div>
                    </div>
                    <div
                        className={clsx([
                            "flex items-center lg:justify-center flex-1 lg:first:justify-start lg:last:justify-end group",
                            "after:hidden before:hidden after:lg:block before:lg:block",
                            "first:after:content-[''] first:after:w-full first:after:bg-slate-300/60 first:after:h-[2px] first:after:ml-5 group-[.mode--light]:first:after:bg-slate-300/20",
                            "last:before:content-[''] last:before:w-full last:before:bg-slate-300/60 last:before:h-[2px] last:before:mr-5 group-[.mode--light]:last:before:bg-slate-300/20",
                            "last:after:hidden after:content-[''] after:w-full after:bg-slate-300/60 after:h-[2px] after:ml-5 group-[.mode--light]:after:bg-slate-300/20",
                            "first:before:hidden before:content-[''] before:w-full before:bg-slate-300/60 before:h-[2px] before:mr-5 group-[.mode--light]:before:bg-slate-300/20",
                        ])}
                    />
                </div>

                <div className="mt-7">
                    <div className="flex flex-col box box--stacked">
                        <div className="p-7">            
                            {/* === Company Name === */}
                            <div className="flex-col block pt-5 mt-5 xl:items-center sm:flex xl:flex-row first:mt-0 first:pt-0">
                                <label className="inline-block mb-2 sm:mb-0 sm:mr-5 sm:text-right xl:w-60 xl:mr-14">
                                    <div className="text-left">
                                        <div className="flex items-center">
                                            <div className="font-medium">Project Name</div>
                                            <div className="ml-2.5 px-2 py-0.5 bg-slate-100 text-slate-500 dark:bg-darkmode-300 dark:text-slate-400 text-xs rounded-md border border-slate-200">
                                                Required
                                            </div>
                                        </div>
                                    </div>
                                </label>
                                <div className="flex-1 w-full mt-3 xl:mt-0">
                                    <div className="flex flex-col items-center md:flex-row">
                                        <FormInput
                                            type="text"
                                            name="name"
                                            required
                                            placeholder="e.g., Sales Campaign"
                                            value={formData.name}
                                            onChange={handleChange}
                                            disabled={loading}
                                        />
                                    </div>
                                </div>
                            </div>
                            <div className="flex-col block pt-5 mt-5 xl:items-center sm:flex xl:flex-row first:mt-0 first:pt-0">
                                <label className="inline-block mb-2 sm:mb-0 sm:mr-5 sm:text-right xl:w-60 xl:mr-14">
                                    <div className="text-left">
                                        <div className="flex items-center">
                                            <div className="font-medium">Start Date</div>
                                            <div className="ml-2.5 px-2 py-0.5 bg-slate-100 text-slate-500 dark:bg-darkmode-300 dark:text-slate-400 text-xs rounded-md border border-slate-200">
                                                Required
                                            </div>
                                        </div>
                                        <div className="mt-1.5 xl:mt-3 text-xs leading-relaxed text-slate-500/80 dark:text-slate-400">
                                            Project Start Date
                                        </div>
                                    </div>
                                </label>
                                <div className="flex-1 w-full mt-3 xl:mt-0">
                                    <Litepicker
                                        value={formData.startDate || ""}
                                        onChange={(e) => {
                                            const selected = e.target.value;
                                            try {
                                                if (selected) {
                                                    const fixedDate = parseLocalDateOnly(selected);
                                                    setFormData((prev) => ({ ...prev, startDate: fixedDate }));
                                                }
                                            } catch (err) {
                                                console.error("Start date parse error:", err);
                                            }
                                        }}


                                        options={{
                                            autoApply: true,
                                            dropdowns: {
                                                minYear: 1990,
                                                maxYear: null,
                                                months: true,
                                                years: true,
                                            },
                                        }}
                                    />
                                </div>
                            </div>
                            <div className="flex-col block pt-5 mt-5 xl:items-center sm:flex xl:flex-row first:mt-0 first:pt-0">
                                <label className="inline-block mb-2 sm:mb-0 sm:mr-5 sm:text-right xl:w-60 xl:mr-14">
                                    <div className="text-left">
                                        <div className="flex items-center">
                                            <div className="font-medium">End Date</div>
                                            <div className="ml-2.5 px-2 py-0.5 bg-slate-100 text-slate-500 dark:bg-darkmode-300 dark:text-slate-400 text-xs rounded-md border border-slate-200">
                                                Required
                                            </div>
                                        </div>
                                        <div className="mt-1.5 xl:mt-3 text-xs leading-relaxed text-slate-500/80 dark:text-slate-400">
                                            Project End Date
                                        </div>
                                    </div>
                                </label>
                                <div className="flex-1 w-full mt-3 xl:mt-0">
                                    <Litepicker
                                        value={formData.endDate || ""}
                                        onChange={(e) => {
                                            const selected = e.target.value;
                                            try {
                                                if (selected) {
                                                    const fixedDate = parseLocalDateOnly(selected);
                                                    setFormData((prev) => ({ ...prev, endDate: fixedDate }));
                                                }
                                            } catch (err) {
                                                console.error("End date parse error:", err);
                                            }
                                        }}

                                        options={{
                                            minDate: formData.startDate || undefined,
                                            autoApply: true,
                                            dropdowns: {
                                                minYear: 1990,
                                                maxYear: null,
                                                months: true,
                                                years: true,
                                            },
                                        }}
                                    />
                                </div>
                            </div>

                            {error && (
                                <div className="mt-6 text-danger text-sm">{error}</div>
                            )}
                        </div>

                        <div className="flex flex-col-reverse gap-3 py-5 border-t md:flex-row md:justify-end px-7 border-slate-200/80">
                            {/* Cancel Button */}
                            <Button
                                type="button"
                                variant="outline-secondary"
                                className="w-full px-10 md:w-auto"
                                onClick={() => navigate(-1)} // Go back to previous page
                                disabled={loading}
                            >
                                Cancel
                            </Button>

                            {/* Save/Create Button */}
                            <Button
                                type="button"
                                variant="outline-primary"
                                className="w-full px-10 md:w-auto border-primary/50"
                                onClick={handleSubmit}
                                disabled={loading}
                            >
                                <Lucide icon="Pocket" className="stroke-[1.3] w-4 h-4 mr-2 -ml-2" />
                                {mode === "edit" ? "Save Changes" : "Create"}
                            </Button>
                        </div>

                    </div>
                </div>
            </div>
        </div>
    );
}
